import { Container, Label } from '@playcanvas/pcui';
import { Mat4, Vec3 } from 'playcanvas';

import { DataPanel } from './data-panel';
import { Events } from '../events';
import { AuthService } from '../auth';
import { BottomToolbar } from './bottom-toolbar';
import { ColorPanel } from './color-panel';
import { ExportPopup } from './export-popup';
import { ImageSettingsDialog } from './image-settings-dialog';
import { localize, localizeInit } from './localization';
import { Menu } from './menu';
import { ModeToggle } from './mode-toggle';
import { LoginDialog } from './login-dialog';
import { RemoteScenesDialog } from './remote-scenes';
import { UploadDialog } from './upload-dialog';
import logo from './biligoba-logo.png';
import { Popup, ShowOptions } from './popup';
import { Progress } from './progress';
import { PublishSettingsDialog } from './publish-settings-dialog';
import { RightToolbar } from './right-toolbar';
import { PictureButton } from './picture-button';
import { MeasureButton } from './measure-button';
import { MovementControls } from './movement-controls';
import { ScenePanel } from './scene-panel';
import { ShortcutsPopup } from './shortcuts-popup';
import { Spinner } from './spinner';
import { TimelinePanel } from './timeline-panel';
import { Tooltips } from './tooltips';
import { VideoSettingsDialog } from './video-settings-dialog';
import { ViewCube } from './view-cube';
import { ViewPanel } from './view-panel';
import { version } from '../../package.json';
import { AssociatedPicturePanel } from './associated-picture-panel';
import { EvidencePanel } from './evidence-panel';
import { SampleScenesDialog } from './sample-scenes-dialog';

class EditorUI {
    appContainer: Container;
    topContainer: Container;
    canvasContainer: Container;
    toolsContainer: Container;
    canvas: HTMLCanvasElement;
    popup: Popup;

    constructor(events: Events) {
        localizeInit();

        // favicon
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = logo;
        document.head.appendChild(link);

        // app
        const appContainer = new Container({
            id: 'app-container'
        });

        // editor
        const editorContainer = new Container({
            id: 'editor-container'
        });

        // tooltips container
        const tooltipsContainer = new Container({
            id: 'tooltips-container'
        });

        // top container
        const topContainer = new Container({
            id: 'top-container'
        });

        // canvas
        const canvas = document.createElement('canvas');
        canvas.id = 'canvas';

        // app label
        const appLabel = new Label({
            id: 'app-label',
            text: `SUPERSPLAT v${version}`
        });

        // cursor label
        // const cursorLabel = new Label({
        //     id: 'cursor-label'
        // });

        let fullprecision = '';

        // events.on('camera.focalPointPicked', (details: { position: Vec3 }) => {
        //     cursorLabel.text = `${details.position.x.toFixed(2)}, ${details.position.y.toFixed(2)}, ${details.position.z.toFixed(2)}`;
        //     fullprecision = `${details.position.x}, ${details.position.y}, ${details.position.z}`;
        // });

        // ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
        //     cursorLabel.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        // });

        // cursorLabel.dom.addEventListener('pointerdown', () => {
        //     navigator.clipboard.writeText(fullprecision);

        //     const orig = cursorLabel.text;
        //     cursorLabel.text = localize('cursor.copied');
        //     setTimeout(() => {
        //         cursorLabel.text = orig;
        //     }, 1000);
        // });

        // canvas container
        const canvasContainer = new Container({
            id: 'canvas-container'
        });

        // tools container
        const toolsContainer = new Container({
            id: 'tools-container'
        });

        // tooltips
        const tooltips = new Tooltips();
        tooltipsContainer.append(tooltips);

        // bottom toolbar
        // const scenePanel = new ScenePanel(events, tooltips);
        const viewPanel = new ViewPanel(events, tooltips);
        const colorPanel = new ColorPanel(events, tooltips);
        // const bottomToolbar = new BottomToolbar(events, tooltips);
        const rightToolbar = new RightToolbar(events, tooltips);
        const pictureButton = new PictureButton(events, tooltips);
        const measureButton = new MeasureButton(events, tooltips);
        const movementControls = new MovementControls(events, tooltips);
        // const modeToggle = new ModeToggle(events, tooltips);
        const authService = new AuthService(events);
        const menu = new Menu(events, authService);
        const evidencePanel = new EvidencePanel(events);

        canvasContainer.dom.appendChild(canvas);
        // canvasContainer.append(appLabel);
        // canvasContainer.append(cursorLabel);
        canvasContainer.append(toolsContainer);
        // canvasContainer.append(scenePanel);
        canvasContainer.append(viewPanel);
        canvasContainer.append(colorPanel);
        // canvasContainer.append(bottomToolbar);
        canvasContainer.append(rightToolbar);
        canvasContainer.append(evidencePanel);
        canvasContainer.append(pictureButton);
        canvasContainer.append(measureButton);
        canvasContainer.append(movementControls);
        // canvasContainer.append(modeToggle);
        canvasContainer.append(menu);

        // view axes container
        // const viewCube = new ViewCube(events);
        // canvasContainer.append(viewCube);
        // events.on('prerender', (cameraMatrix: Mat4) => {
        //     viewCube.update(cameraMatrix);
        // });

        // main container
        const mainContainer = new Container({
            id: 'main-container'
        });

        // const timelinePanel = new TimelinePanel(events, tooltips);
        // const dataPanel = new DataPanel(events);

        mainContainer.append(canvasContainer);
        // mainContainer.append(timelinePanel);
        // mainContainer.append(dataPanel);

        editorContainer.append(mainContainer);

        // tooltips.register(cursorLabel, localize('cursor.click-to-copy'), 'top');

        // message popup
        const popup = new Popup(tooltips);

        // shortcuts popup
        const shortcutsPopup = new ShortcutsPopup();

        // export popup
        const exportPopup = new ExportPopup(events);

        // publish settings
        const publishSettingsDialog = new PublishSettingsDialog(events);

        // image settings
        const imageSettingsDialog = new ImageSettingsDialog(events);

        // video settings
        const videoSettingsDialog = new VideoSettingsDialog(events);

        // login dialog
        const loginDialog = new LoginDialog(events, authService);

        // remote scenes & upload dialogs
        const remoteScenesDialog = new RemoteScenesDialog(events, authService);
        const uploadDialog = new UploadDialog(events, authService);

        // associated image panel
        const associatedPicturePanel = new AssociatedPicturePanel(events);

        // initial sample scene chooser
        const sampleScenesDialog = new SampleScenesDialog(events);

        topContainer.append(popup);
        topContainer.append(exportPopup);
        topContainer.append(publishSettingsDialog);
        topContainer.append(imageSettingsDialog);
        topContainer.append(videoSettingsDialog);
        topContainer.append(loginDialog);
        topContainer.append(remoteScenesDialog);
        topContainer.append(uploadDialog);
        topContainer.append(sampleScenesDialog);

        appContainer.append(editorContainer);
        appContainer.append(topContainer);
        appContainer.append(tooltipsContainer);
        appContainer.append(shortcutsPopup);
        appContainer.append(associatedPicturePanel);

        this.appContainer = appContainer;
        this.topContainer = topContainer;
        this.canvasContainer = canvasContainer;
        this.toolsContainer = toolsContainer;
        this.canvas = canvas;
        this.popup = popup;

        document.body.appendChild(appContainer.dom);
        document.body.setAttribute('tabIndex', '-1');

        events.on('show.shortcuts', () => {
            shortcutsPopup.hidden = false;
        });

        events.on('show-associated-picture', () => {
            associatedPicturePanel.hidden = false;
        })

        events.function('show.exportPopup', (exportType, splatNames: [string], showFilenameEdit: boolean) => {
            return exportPopup.show(exportType, splatNames, showFilenameEdit);
        });

        events.function('show.imageSettingsDialog', async () => {
            const imageSettings = await imageSettingsDialog.show();

            if (imageSettings) {
                await events.invoke('render.image', imageSettings);
            }
        });

        events.on('user.login', () => {
            loginDialog.show();
        });

        events.on('user.logout', () => {
            authService.logout();
        });

        events.on('user.showScenes', () => {
            if (authService.isAuthenticated()) {
                remoteScenesDialog.show();
            } else {
                loginDialog.show();
            }
        });

        events.function('user.uploadScene', () => {
            return uploadDialog.show();
        });

        events.function('show.videoSettingsDialog', async () => {
            const videoSettings = await videoSettingsDialog.show();

            if (videoSettings) {
                await events.invoke('render.video', videoSettings);
            }
        });

        events.function('show.about', () => {
            return this.popup.show({
                type: 'info',
                header: 'About',
                message: `
                    Biligoba Scene Forensics v${version}<br><br>
                    Copyright (c) 2019-2025 Biligoba Technology.<br>
                    All rights reserved.<br><br>
                    www.biligoba.com<br><br>
                    This application includes components derived from the PlayCanvas supersplat viewer, 
                    provided under the terms of the MIT License.<br><br>
                    Copyright (c) 2011-2024 PlayCanvas Ltd.<br><br>
                    Permission is hereby granted, free of charge, to any person obtaining a copy of 
                    this software and associated documentation files (the "Software"), to deal in 
                    the Software without restriction, including without limitation the rights to 
                    use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies 
                    of the Software, and to permit persons to whom the Software is furnished to do 
                    so, subject to the following conditions:<br><br>

                    The above copyright notice and this permission notice shall be included in all 
                    copies or substantial portions of the Software.<br><br>

                    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR 
                    IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS 
                    FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR 
                    COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER 
                    IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN 
                    CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
                    `
            });
        });

        events.function('showPopup', (options: ShowOptions) => {
            return this.popup.show(options);
        });

        // spinner

        const spinner = new Spinner();

        topContainer.append(spinner);

        events.on('startSpinner', () => {
            spinner.hidden = false;
        });

        events.on('stopSpinner', () => {
            spinner.hidden = true;
        });

        // progress

        const progress = new Progress();

        topContainer.append(progress);

        events.on('progressStart', (header: string) => {
            progress.hidden = false;
            progress.setHeader(header);
            progress.setOnCancel(null);
        });

        events.on('progressUpdate', (options: { text: string, progress: number }) => {
            progress.setText(options.text);
            progress.setProgress(options.progress);
        });

        events.on('progressEnd', () => {
            progress.hidden = true;
            progress.setOnCancel(null);
        });

        events.on('progressShowCancel', (fn: () => void) => {
            progress.setOnCancel(fn);
        });

        // initialize canvas to correct size before creating graphics device etc
        const pixelRatio = window.devicePixelRatio;
        canvas.width = Math.ceil(canvasContainer.dom.offsetWidth * pixelRatio);
        canvas.height = Math.ceil(canvasContainer.dom.offsetHeight * pixelRatio);

        ['contextmenu', 'gesturestart', 'gesturechange', 'gestureend'].forEach((event) => {
            document.addEventListener(event, (e) => {
                e.preventDefault();
            }, true);
        });

        // whenever the canvas container is clicked, set keyboard focus on the body
        canvasContainer.dom.addEventListener('pointerdown', (event: PointerEvent) => {
            // set focus on the body if user is busy pressing on the canvas or a child of the tools
            // element
            if (event.target === canvas || toolsContainer.dom.contains(event.target as Node)) {
                document.body.focus();
            }
        }, true);
    }
}

export { EditorUI };
